import { motion } from 'framer-motion';
import { Music, Video, Upload, AlertCircle } from 'lucide-react';

export function UploadSection({
  artistData,
  subscriptionTier,
  canUploadMusic,
  canUploadVideo,
  onMusicUploadClick,
  onVideoUploadClick
}) {
  const musicLimit = subscriptionTier.features[0];
  const videoLimit = subscriptionTier.features[1];

  const musicUploaded = artistData.uploadedMusic || 0;
  const videoUploaded = artistData.uploadedVideos || 0;
  const musicMaxLimit = musicLimit.unlimited ? '∞' : musicLimit.limit;
  const videoMaxLimit = videoLimit.unlimited ? '∞' : videoLimit.limit;

  const musicPercentage = musicLimit.unlimited ? 0 : (musicUploaded / musicLimit.limit) * 100;
  const videoPercentage = videoLimit.unlimited ? 0 : (videoUploaded / videoLimit.limit) * 100;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Music Upload */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white border-2 border-purple-200 rounded-xl p-6 hover:shadow-lg transition"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-purple-100 p-3 rounded-lg">
            <Music size={24} className="text-purple-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900">Upload Music</h3>
            <p className="text-sm text-gray-600">
              {musicUploaded} / {musicMaxLimit}
            </p>
          </div>
        </div>

        {/* Progress Bar */}
        {!musicLimit.unlimited && (
          <div className="mb-4">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <motion.div
                className={`h-2 rounded-full transition-all ${
                  musicPercentage >= 100 ? 'bg-red-500' : musicPercentage >= 75 ? 'bg-yellow-500' : 'bg-purple-500'
                }`}
                initial={{ width: 0 }}
                animate={{ width: `${Math.min(musicPercentage, 100)}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {Math.round(musicPercentage)}% of quota used
            </p>
          </div>
        )}

        {canUploadMusic() ? (
          <button
            onClick={onMusicUploadClick}
            className="w-full py-3 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg font-semibold transition flex items-center justify-center gap-2"
          >
            <Upload size={18} />
            Upload Music Track
          </button>
        ) : (
          <div className="bg-red-50 border border-red-200 p-3 rounded-lg flex gap-2 items-start">
            <AlertCircle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-red-900">Quota Reached</p>
              <p className="text-xs text-red-700">You've uploaded {musicUploaded} of {musicMaxLimit} music tracks</p>
            </div>
          </div>
        )}
      </motion.div>

      {/* Video Upload */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white border-2 border-blue-200 rounded-xl p-6 hover:shadow-lg transition"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-blue-100 p-3 rounded-lg">
            <Video size={24} className="text-blue-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900">Upload Video</h3>
            <p className="text-sm text-gray-600">
              {videoUploaded} / {videoMaxLimit}
            </p>
          </div>
        </div>

        {/* Progress Bar */}
        {!videoLimit.unlimited && (
          <div className="mb-4">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <motion.div
                className={`h-2 rounded-full transition-all ${
                  videoPercentage >= 100 ? 'bg-red-500' : videoPercentage >= 75 ? 'bg-yellow-500' : 'bg-blue-500'
                }`}
                initial={{ width: 0 }}
                animate={{ width: `${Math.min(videoPercentage, 100)}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {Math.round(videoPercentage)}% of quota used
            </p>
          </div>
        )}

        {canUploadVideo() ? (
          <button
            onClick={onVideoUploadClick}
            className="w-full py-3 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg font-semibold transition flex items-center justify-center gap-2"
          >
            <Upload size={18} />
            Upload Video
          </button>
        ) : (
          <div className="bg-red-50 border border-red-200 p-3 rounded-lg flex gap-2 items-start">
            <AlertCircle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-red-900">Quota Reached</p>
              <p className="text-xs text-red-700">You've uploaded {videoUploaded} of {videoMaxLimit} videos</p>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}